import { createId } from '@paralleldrive/cuid2'
import {
  foldEvents,
  projectRow,
  statoAndRepairOf,
  statoOf,
  synthesizeFromRow,
} from 'api'
import type { EventFold, InterventoEvento, RepairSummary, Stato } from 'api'

import type { InterventoDoc } from '@/db/local/dexie-db'

/**
 * An intervento's events: those it carries, or those its row implies when it was
 * pulled before the app knew events (the same ids the server stored for it).
 */
export const eventsOf = (doc: InterventoDoc): InterventoEvento[] =>
  doc.eventi ?? synthesizeFromRow(doc)

/** The intervento's history: its inspection and repairs, as the server folds them. */
export const foldOf = (doc: InterventoDoc): EventFold =>
  foldEvents(eventsOf(doc))

/** Where the intervento stands, from its events (its row without them). */
export const statoOfDoc = (doc: InterventoDoc): Stato =>
  statoOf(eventsOf(doc), doc)

/** The stato and the repairs, from one fold. */
export const statoAndRepairOfDoc = (
  doc: InterventoDoc
): { stato: Stato; repair: RepairSummary } =>
  statoAndRepairOf(eventsOf(doc), doc)

/** Whether the intervento records a repair start: none can be open or closed without. */
export const hasRepairEvents = (doc: InterventoDoc): boolean =>
  !!doc.eventi?.some((evento) => evento.tipo === 'riparazione_avviata')

/**
 * When the inspection started and was completed: the taps on Avvia and Completa,
 * or the row's `data` and `updated_at` for a row without events (no operatore to
 * record them as).
 */
export function inspectionTimes(doc: InterventoDoc): {
  startedAt: Date | null
  completedAt: Date | null
} {
  const inspection = foldOf(doc).phases.find(
    (phase) => phase.kind === 'inspection'
  )
  if (inspection) {
    return { startedAt: inspection.startedAt, completedAt: inspection.closedAt }
  }
  return {
    startedAt: doc.status !== 'bozza' && doc.data ? new Date(doc.data) : null,
    completedAt:
      doc.status === 'completato' && doc.updated_at
        ? new Date(doc.updated_at)
        : null,
  }
}

export type NewEvent = Pick<InterventoEvento, 'tipo' | 'operatore_id'> &
  Partial<InterventoEvento>

/**
 * The intervento with one more event, and the status, esito and current
 * operatore that follow, as the server folds them. No updated_at: the sync hooks
 * mark the write as unsent and stamp it.
 */
export function appendEvent(
  doc: InterventoDoc,
  event: NewEvent
): InterventoDoc {
  const events = [
    ...eventsOf(doc),
    {
      id: createId(),
      intervento_id: doc.id,
      riparazione_id: null,
      esito: null,
      note: null,
      created_at: new Date(),
      ...event,
    },
  ]
  return { ...doc, eventi: events, ...projectRow(events, doc) }
}
